import TextSnippetTwoToneIcon from '@mui/icons-material/TextSnippetTwoTone'
import React, { Fragment, useEffect, useState } from 'react'
import { Button, Modal, SvgIcon } from '../../../../components'
import { useModal } from '../../../../utils/hooks'
import { APP_CONFIG, APP_VERSION, APP_VERSION_STORAGE_KEY } from '../../common/constants'
import { loadValue, saveValue } from '../../common/localStorage'
import Accordion from '../common/Accordion'
import { useSelector } from 'react-redux'
import { getGlobalSettingsState } from '../../../../utils/selectors'

const initOpenedAccordions = Object.values(APP_VERSION)
const appCurrentVersion = APP_VERSION.CURRENT

const AboutModal = () => {
  const [show, toggleModal] = useModal(false)
  const [openedAccordions, setOpenedAccordions] = useState([appCurrentVersion])
  const storedVersion = loadValue(APP_VERSION_STORAGE_KEY)
  const isEulaAccepted = useSelector((state) => getGlobalSettingsState(state).isEulaAccepted)

  const toggleAccordions = () => {
    setOpenedAccordions(openedAccordions.length ? [] : initOpenedAccordions)
  }

  const handleToggleModal = () => {
    toggleModal()
    setOpenedAccordions([appCurrentVersion])
  }

  useEffect(() => {
    if (isEulaAccepted && storedVersion !== appCurrentVersion) {
      toggleModal()
      setOpenedAccordions([appCurrentVersion])
    }
  }, [])

  useEffect(() => {
    if (isEulaAccepted) {
      saveValue(APP_VERSION_STORAGE_KEY, APP_CONFIG.appVersion)
    }
  }, [isEulaAccepted])

  return (
    <Fragment>
      <Button variant="topNav" className="button topNav__button" onClick={handleToggleModal}>
        <TextSnippetTwoToneIcon className="material-icons-two-tone material-icons-two-tone--noinvert" />
        <span className="button__text">About</span>
      </Button>
      <Modal
        show={show}
        onHide={toggleModal}
        className="-about"
        enforceFocus={false} // to allow focus on Feedback Inline Manual when two modals are opend at the same time
        header={
          <Modal.Header closeButton>
            <div className="appBrandingGroup">
              <SvgIcon src={APP_CONFIG.logoUrl} svgClasses="appLogo" alt="App Logo" />
              <div className="aboutAppHeading">
                <div className="aboutAppAcronym">
                  <span className="aboutAppAcronym__label"></span>
                  {APP_CONFIG.appCode}
                </div>
                <div className="aboutAppFullName">{APP_CONFIG.appName}</div>
              </div>
            </div>
          </Modal.Header>
        }
      >
        <p className="intro">
          Find the most cost-effective joist, truss and multi-truss hangers based on installation
          type, hanger options and demand load, with the assistance of 3D visualization. This
          application is the web-based version of the Joist, Truss, and Multi-Truss tabs in
          Connector Selector.
        </p>

        <div className="toggle-accordion">
          <span className="material-icons" onClick={toggleAccordions}>
            {openedAccordions.length ? 'unfold_less' : 'unfold_more'}
          </span>
        </div>

        <Accordion.Root
          type="multiple"
          value={openedAccordions}
          onValueChange={setOpenedAccordions}
        >
          <Accordion.Item
            value={appCurrentVersion}
            title={
              <h4>
                <span className="release-number">{appCurrentVersion}</span>
                <span className="release-date">Released on August 07, 2025</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Add a customer satisfaction survey</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">
                  [CAN] Fixed an issue where TJC&#39;s were not being filtered out of the output
                  based on loads
                </div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">
                  [US] Fixed an issue where THA&#39;s were not being shown for the Glulam header
                  with Floor Truss
                </div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.5.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.5.0']}</span>
                <span className="release-date">Released on June 12, 2025</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Set hanger minimum skew of 3 degrees before triggering specials nomenclature
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Update nail quantity on HU4.12/11</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Update LEG & MEG Skew and Top Flange Offset options
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Prevent HHUS for skewed trusses without bevel cuts
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Add structural steel header options to HWP and HWPH hangers
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Add taller SPF 4x header options</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Add WP4.68X</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Add wider steel headers to work with DGT hangers</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Add GluLam sizes of DGHT&apos;s</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Add more skew options to THJU multi truss hangers
                </div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.4.2']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.4.2']}</span>
                <span className="release-date">Released on January 09, 2025</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/gear.svg" alt="Change Icon" />
                  Change
                </div>
                <div className="release-info">Maintenance</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Added additional Structural Steel Header Sizes. 3.5&quot; and 4&quot;
                </div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.4.1']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.4.1']}</span>
                <span className="release-date">Released on November 14, 2024</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/gear.svg" alt="Change Icon" />
                  Change
                </div>
                <div className="release-info">Maintenance</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.4.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.4.0']}</span>
                <span className="release-date">Released on August 08, 2024</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.3.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.3.0']}</span>
                <span className="release-date">Released on January 18, 2024</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Updates to Database based on the Wood Construction Connectors Catalog{' '}
                  <a
                    target="_blank"
                    href="https://www.strongtie.com/resources/literature/wood-construction-connectors-catalog"
                    rel="noreferrer"
                  >
                    (C-C-2024)
                  </a>
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Added DGT Firewall hangers</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Added HGUS wide sizes for EWP</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Added HWPH8x&apos;s</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  WMU hangers now offer both Masonry Mid-Wall and Masonry Top-of-Wall applications
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">EG glulam hanger loads adjusted to match catalog</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  HB2-2x and 3x nailer loads updated per new testing
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  HGLT and HGLTV now allow combined slope &amp; skew
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  HWPH now limits combined slope &amp; skews to no more than 45 skew
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  IUS 2-7/16 size hangers no longer limited to special 2-1/4&quot; loads
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  LRUZ slope limits have been updated to match the catalog
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  THAC now show when &apos;concealed&apos; style is selected
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Added more Glulam sizes in solid sawn widths such as 5-1/2 etc.
                </div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Removed 2-11/16&quot; wide PSL sizes</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.2.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.2.0']}</span>
                <span className="release-date">Released on November 02, 2023</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.1.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.1.0']}</span>
                <span className="release-date">Released on August 18, 2022</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-newFeature">
                  <SvgIcon src="assets/images/iconic/star-empty.svg" alt="New Feature" />
                  New Feature
                </div>
                <div className="release-info">Added “Job List” functionality</div>
              </li>

              <li>
                <div className="release-type release-newFeature">
                  <SvgIcon src="assets/images/iconic/star-empty.svg" alt="New Feature" />
                  New Feature
                </div>
                <div className="release-info">Added “Download CSV” export</div>
              </li>

              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Updated API Connection</div>
              </li>

              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Updated fastener descriptions with hyperlinks</div>
              </li>

              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">
                  Print output only shows what&apos;s in the filtered output, including the
                  Optimized Models and Search filters
                </div>
              </li>

              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Added installation graphic to the Print output</div>
              </li>

              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" alt="Change" />
                  Change
                </div>
                <div className="release-info">Full hanger model now shown</div>
              </li>

              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix" />
                  Bug Fix
                </div>
                <div className="release-info">Missing top flange fasteners</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix" />
                  Bug Fix
                </div>
                <div className="release-info">I-Joist Web Stiffeners</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix" />
                  Bug Fix
                </div>
                <div className="release-info">Nailer Header</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.0.1']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.0.1']}</span>
                <span className="release-date">Released on May 09, 2022</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>
          </Accordion.Item>
          <Accordion.Item
            value={APP_VERSION['1.0.0']}
            title={
              <h4>
                <span className="release-number">{APP_VERSION['1.0.0']}</span>
                <span className="release-date">Released on March 31, 2022</span>
              </h4>
            }
          >
            <ul className="release-note">
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/magic-wand.svg" alt="Initial" />
                  Initial
                </div>
                <div className="release-info">Initial release of the application</div>
              </li>
            </ul>
          </Accordion.Item>
        </Accordion.Root>
      </Modal>
    </Fragment>
  )
}

export default AboutModal
